import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchVanchiByDateRange, fetchReportStats } from '../../store/slices/vanchiSlice';
import { fetchVanchiNames } from '../../store/slices/mastersSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import StatsCard from '../../common/components/StatsCard';
import DataTable from '../../common/components/DataTable';
import { SimplePaginationInfo } from '../../types';
import WarningIcon from '../../common/assets/icons/warning.svg';
import SuccessIcon from '../../common/assets/icons/success.svg';
import ErrorIcon from '../../common/assets/icons/error.svg';
import { exportToPDF, exportToExcel, generateFilename, formatDataForExport, ExportColumn } from '../../common/utils/exportUtils';
import { mastersService } from '../../services/mastersService';
import { ValuesetParam } from '../../common/utils/valueset';
import { UserRole } from '../../types';
import { toast } from '../../common/utils/toast';

const Report: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list: vanchiList, stats, loading } = useAppSelector((state) => state.vanchi);
  const { vanchiNames: vanchiNamesList } = useAppSelector((state) => state.masters);
  const { list: usersList } = useAppSelector((state) => state.users);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    bagStatus: '',
    staffName: '',
    nadaName: '',
    vanchiName: '',
    vanchiNumber: '',
    periodicReport: 'recent', // Default to recent transactions
    fromDate: '',
    toDate: ''
  });
  const [showTable, setShowTable] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Dropdown data states
  const [packStatusList, setPackStatusList] = useState<any[]>([]);
  const [nadaList, setNadaList] = useState<any[]>([]);
  const [filteredVanchiNames, setFilteredVanchiNames] = useState<any[]>([]);
  const [filteredVanchiNumbers, setFilteredVanchiNumbers] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);

  // Helper function to filter data based on current filter values
  const applyFilters = (data: any[]) => {
    if (!data || data.length === 0) return [];
    
      return data.filter((item) => {
        // Filter by bag status (using packstatus name from API response)
        if (filters.bagStatus && item.packstatus !== filters.bagStatus) {
          return false;
        }
      
      // Filter by staff name (sealed by)
      if (filters.staffName && item.packby !== filters.staffName) {
        return false;
      }
      
      // Filter by nada name (matching nadacd in API response)
      if (filters.nadaName && item.nadacd !== filters.nadaName) {
        return false;
      }
      
      // Filter by vanchi name
      if (filters.vanchiName && item.vanchicd !== filters.vanchiName) {
        return false;
      }
      
      // Filter by vanchi number
      if (filters.vanchiNumber && item.vanchino !== filters.vanchiNumber) {
        return false;
      }
      
      return true;
    });
  };

  // Helper function to calculate date ranges based on periodic selection
  const calculateDateRange = (periodicType: string) => {
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    switch (periodicType) {
      case 'daily':
        // Today only
        return {
          fromDate: formatDate(today),
          toDate: formatDate(today)
        };

      case 'weekly':
        // This week (Monday to Sunday)
        const startOfWeek = new Date(today);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        startOfWeek.setDate(diff);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        return {
          fromDate: formatDate(startOfWeek),
          toDate: formatDate(endOfWeek)
        };

      case 'monthly':
        // This month (1st to last day)
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        return {
          fromDate: formatDate(startOfMonth),
          toDate: formatDate(endOfMonth)
        };

      case 'yearly':
        // This year (Jan 1st to Dec 31st)
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);

        return {
          fromDate: formatDate(startOfYear),
          toDate: formatDate(endOfYear)
        };

      case 'custom':
        // Don't set dates for custom - let user input them
        return {
          fromDate: '',
          toDate: ''
        };

      default:
        // Recent Transactions - last 7 days
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);

        return {
          fromDate: formatDate(lastWeek),
          toDate: formatDate(today)
        };
    }
  };

  useEffect(() => {
    // Set initial date range based on default periodic selection (recent transactions - last 7 days)
    const dateRange = calculateDateRange('recent');
    setFilters(prev => ({
      ...prev,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate
    }));

    // Load dropdown data
    loadDropdownData();

    // Load vanchi names for filtering
    dispatch(fetchVanchiNames());

    // Load users for staff dropdown
    dispatch(fetchUsers());
    // Don't auto-fetch data on page load - wait for user to click search
  }, [dispatch]);

  // Load dropdown data from valueset API
  const loadDropdownData = async () => {
    try {
      // Load pack status
      const packStatusData = await mastersService.getValuesByParam(ValuesetParam.PACK_STATUS);
      setPackStatusList(packStatusData.data);

      // Load nada list
      const nadaData = await mastersService.getValuesByParam(ValuesetParam.NADA);
      setNadaList(nadaData.data);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  // Filter vanchi names based on selected nada
  useEffect(() => {
    if (vanchiNamesList && filters.nadaName) {
      const filtered = vanchiNamesList.filter((vanchi: any) =>
        vanchi.nadacd === filters.nadaName || vanchi.nadaname === filters.nadaName
      );
      setFilteredVanchiNames(filtered);

      // Reset vanchi name and number when nada changes
      setFilters(prev => ({
        ...prev,
        vanchiName: '',
        vanchiNumber: ''
      }));
    } else {
      setFilteredVanchiNames(vanchiNamesList || []);
    }
  }, [vanchiNamesList, filters.nadaName]);

  // Filter vanchi numbers based on selected vanchi name
  useEffect(() => {
    if (filteredVanchiNames && filters.vanchiName) {
      const selectedVanchi = filteredVanchiNames.find((vanchi: any) =>
        vanchi.vanchiname === filters.vanchiName
      );
      if (selectedVanchi) {
        setFilteredVanchiNumbers([selectedVanchi]);

        // Auto-populate vanchi number
        setFilters(prev => ({
          ...prev,
          vanchiNumber: selectedVanchi.vanchino || ''
        }));
      }
    } else {
      setFilteredVanchiNumbers([]);
    }
  }, [filteredVanchiNames, filters.vanchiName]);

  // Filter staff members from users list
  useEffect(() => {
    if (usersList && usersList.length > 0) {
      // Filter users who are active staff members (not admin)
      const staffMembers = usersList.filter((user: any) =>
        user.isactive === 'Y' &&
        user.role &&
        user.role.toLowerCase() !== UserRole.ADMIN.toLowerCase()
      );
      setStaffList(staffMembers);
    }
  }, [usersList]);

  // Apply filters when vanchiList changes (after search)
  useEffect(() => {
    if (vanchiList && vanchiList.length > 0 && showTable) {
      const filtered = applyFilters(vanchiList);
      setFilteredData(filtered);
    } else if (showTable) {
      setFilteredData([]);
    }
  }, [vanchiList, showTable]);

  const handleFilterChange = (field: string, value: string) => {
    if (field === 'periodicReport') {
      // When periodic report changes, automatically set the date range
      const dateRange = calculateDateRange(value);
      setFilters(prev => ({
        ...prev,
        [field]: value,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [field]: value
      }));
    }
    // Don't hide table when filters change - keep current data visible
    // User can see current results while adjusting filters
  };

  const handleClear = () => {
    // Reset to default periodic selection (recent transactions)
    const dateRange = calculateDateRange('recent');
    setFilters({
      bagStatus: '',
      staffName: '',
      nadaName: '',
      vanchiName: '',
      vanchiNumber: '',
      periodicReport: 'recent',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate
    });
    setFilteredData([]);
    setShowTable(false);
    setCurrentPage(1); // Reset to first page
  };

  const handleSearch = () => {
    // Check if date range is provided for packlog API
    if (filters.fromDate && filters.toDate) {
      // Use the new packlog API with date range
      dispatch(fetchVanchiByDateRange({
        startDate: filters.fromDate,
        endDate: filters.toDate
      }));
      dispatch(fetchReportStats({
        startDate: filters.fromDate,
        endDate: filters.toDate
      }));
    } else {
      // Use today's date as default if no date range provided
      const today = new Date().toISOString().split('T')[0];
      dispatch(fetchVanchiByDateRange({ startDate: today, endDate: today }));
      dispatch(fetchReportStats({ startDate: today, endDate: today }));
    }
    
    setShowTable(true);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Export functionality
  const handleExportExcel = async () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        alert('No data available to export');
        return;
      }

      setIsExporting(true);

      // Format data for export
      const formattedData = formatDataForExport(filteredData);

      // Define columns for export (excluding render functions)
      const exportColumns: ExportColumn[] = [
        { key: 'packdt', label: 'Seal Date/Time', width: 20 },
        { key: 'transno', label: 'Transaction No.', width: 15 },
        { key: 'nadaname', label: 'Nada', width: 12 },
        { key: 'vanchiname', label: 'Vanchi', width: 15 },
        { key: 'vanchino', label: 'Vanchi No.', width: 12 },
        { key: 'packno', label: 'Bag No.', width: 10 },
        { key: 'sealedby', label: 'Sealed By', width: 20 },
        { key: 'securityguard', label: 'Security Guard', width: 20 },
        { key: 'receivedt', label: 'Received Date/Time', width: 20 },
        { key: 'receivedby', label: 'Received By', width: 20 },
        { key: 'packstatus', label: 'Status', width: 12 },
        { key: 'packcomments', label: 'Comments', width: 25 }
      ];

      // Generate filename with timestamp
      const filename = generateFilename('vanchi_report', 'xlsx');

      // Generate title based on current filters
      const dateRange = filters.fromDate && filters.toDate
        ? `${filters.fromDate} to ${filters.toDate}`
        : 'All Records';
      const title = `Vanchi Report - ${dateRange}`;

      // Export to Excel
      await exportToExcel(formattedData, exportColumns, title, filename);
      toast.showSuccess('Excel file exported successfully!');

    } catch (error) {
      console.error('Excel export failed:', error);
      toast.showError('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        alert('No data available to export');
        return;
      }

      setIsExporting(true);

      // Format data for export
      const formattedData = formatDataForExport(filteredData);

      // Define columns for export (excluding render functions)
      const exportColumns: ExportColumn[] = [
        { key: 'packdt', label: 'Seal Date/Time', width: 20 },
        { key: 'transno', label: 'Transaction No.', width: 15 },
        { key: 'nadaname', label: 'Nada', width: 12 },
        { key: 'vanchiname', label: 'Vanchi', width: 15 },
        { key: 'vanchino', label: 'Vanchi No.', width: 12 },
        { key: 'packno', label: 'Bag No.', width: 10 },
        { key: 'sealedby', label: 'Sealed By', width: 20 },
        { key: 'securityguard', label: 'Security Guard', width: 20 },
        { key: 'receivedt', label: 'Received Date/Time', width: 20 },
        { key: 'receivedby', label: 'Received By', width: 20 },
        { key: 'packstatus', label: 'Status', width: 12 },
        { key: 'packcomments', label: 'Comments', width: 25 }
      ];

      // Generate filename with timestamp
      const filename = generateFilename('vanchi_report', 'pdf');

      // Generate title based on current filters
      const dateRange = filters.fromDate && filters.toDate
        ? `${filters.fromDate} to ${filters.toDate}`
        : 'All Records';
      const title = `Vanchi Report - ${dateRange}`;

      // Export to PDF
      await exportToPDF(formattedData, exportColumns, title, filename);
      toast.showSuccess('PDF file exported successfully!');

    } catch (error) {
      console.error('PDF export failed:', error);
      toast.showError('Failed to export PDF file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Check if export buttons should be enabled
  // Export is available when table is visible and has filtered data
  // Note: Table stays visible when filters change, so export remains available
  const canExport = showTable && filteredData && filteredData.length > 0;

  // DataTable columns definition (same as vanchiStatus)
  const columns = [
    {
      key: 'sNo',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_value: any, _row: any, rowIndex?: number, pagination?: any) => {
        const start = ((pagination?.page || 1) - 1) * (pagination?.limit || 0);
        return String((start + (rowIndex ?? 0) + 1)).padStart(2, '0');
      }
    },
    {
      key: 'packdt',
      label: 'Seal Date/Time',
      sortable: true,
      width: 160,
      render: (value: string | null) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${formattedDate} / ${formattedTime}`;
        } catch (error) {
          return '-';
        }
      },
    },
    {
      key: 'transno',
      label: 'Trans No.',
      sortable: true,
      width: 100,
    },
    {
      key: 'nadacd',
      label: 'Nada',
      sortable: true,
      width: 80,
    },
    {
      key: 'vanchicd',
      label: 'Vanchi',
      sortable: true,
      width: 80,
    },
    {
      key: 'vanchino',
      label: 'Vanchi No',
      sortable: true,
      width: 90,
    },
    {
      key: 'bagno',
      label: 'Bag.No.',
      sortable: true,
      width: 60,
    },
    {
      key: 'packby',
      label: 'Sealed By',
      sortable: true,
      width: 100,
    },
    {
      key: 'secguard',
      label: 'Security Guard',
      sortable: true,
      width: 110,
    },
    { key: 'spacer', label: '' },
    {
      key: 'receivedt',
      label: 'Received Date/Time',
      sortable: true,
      width: 160,
      render: (value: string | null) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${formattedDate} / ${formattedTime}`;
        } catch (error) {
          return '-';
        }
      },
    },
    {
      key: 'receiveby',
      label: 'Received By',
      sortable: true,
      width: 100,
      render: (value: string) => value || '-',
    },
    {
      key: 'packstatus',
      label: 'Status',
      sortable: true,
      width: 60,
      render: (value: any) => {
        const key = String(value || '').trim().toLowerCase();
        switch (key) {
          case 'in_transit':
            return <WarningIcon width={20} height={20} />;
          case 'received':
            return <SuccessIcon width={20} height={20} />;
          case 'rejected':
            return <ErrorIcon width={20} height={20} />;
          default:
            return <WarningIcon width={20} height={20} />;
        }
      },
    },
    {
      key: 'packcomments',
      label: 'Comments',
      sortable: false,
      width: 120,
      render: (value: string) => value || '-',
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (_column: string, _direction: 'asc' | 'desc') => {
    // setSortColumn(column);
    // setSortDirection(direction);
    // In a real app, this would trigger a new API call with sort parameters
  };

  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  // Calculate total count for pagination based on filtered data
  const totalFilteredCount = filteredData ? filteredData.length : 0;

  return (
    <div className="container-fluid">
      {/* Stats Cards (visible only when table is shown) */}
      {showTable && (
        <div className="d-flex card-stats gap-4 mb-4">
          <div className="">
            <StatsCard
              title="Today"
              packedSent={stats.today.packedSent}
              received={stats.today.received}
              packedSentIcon={<WarningIcon width={15} height={15} />}
            />
          </div>
          <div className="">
            <StatsCard
              title="Yesterday"
              packedSent={stats.yesterday.packedSent}
              received={stats.yesterday.received}
              packedSentIcon={<SuccessIcon width={15} height={15} />}
            />
          </div>
          <div className="">
            <StatsCard
              title="This Week"
              packedSent={stats.week.packedSent}
              received={stats.week.received}
              packedSentIcon={<SuccessIcon width={15} height={15} />}
            />
          </div>
          <div className="">
            <StatsCard
              title="This Month"
              packedSent={stats.month.packedSent}
              received={stats.month.received}
              packedSentIcon={<SuccessIcon width={15} height={15} />}
            />
          </div>
          <div className="">
            <StatsCard
              title="This Year"
              packedSent={stats.year.packedSent}
              received={stats.year.received}
              packedSentIcon={<SuccessIcon width={15} height={15} />}
            />
          </div>
        </div>
      )}

      {/* Download Report Header */}
      <div
        className="w-100 p-2 mb-4 d-flex align-items-center bg-primary"
      >
        <h6 className="text-warning mb-0">DOWNLOAD REPORT</h6>
      </div>

      {/* Filter Fields and Download Options */}
      <div className='reportForm mb-4'>
        {/* Filter Row 1 */}
        <div className="d-flex gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">BAG STATUS</label>
            <select
              className="form-select border-primary"
              value={filters.bagStatus}
              onChange={(e) => handleFilterChange('bagStatus', e.target.value)}
            >
              <option value="">Bag Status</option>
              {packStatusList.map((status, index) => (
                <option key={status.valuesetcode || index} value={status.valuesetnameen || status.valuesetnameml || status.valuesetcode}>
                  {status.valuesetnameen || status.valuesetnameml || status.valuesetcode}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">STAFF NAME</label>
            <select
              className="form-select border-primary"
              value={filters.staffName}
              onChange={(e) => handleFilterChange('staffName', e.target.value)}
            >
              <option value="">Select Staff Name</option>
              {staffList.map((staff, index) => (
                <option key={staff.tblid || index} value={staff.userid}>
                  {staff.firstname} {staff.lastname} ({staff.role})
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">NADA NAME</label>
            <select
              className="form-select border-primary"
              value={filters.nadaName}
              onChange={(e) => handleFilterChange('nadaName', e.target.value)}
            >
              <option value="">Select Nada Name</option>
              {nadaList.map((nada, index) => (
                <option key={nada.valuesetcode || index} value={nada.valuesetnameen || nada.valuesetnameml || nada.valuesetcode}>
                  {nada.valuesetnameen || nada.valuesetnameml || nada.valuesetcode}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">VANCHI NAME</label>
            <select
              className="form-select border-primary"
              value={filters.vanchiName}
              onChange={(e) => handleFilterChange('vanchiName', e.target.value)}
            >
              <option value="">Select Vanchi Name</option>
              {filteredVanchiNames.map((vanchi, index) => (
                <option key={vanchi.tblid || index} value={vanchi.vanchiname}>
                  {vanchi.vanchiname}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">VANCHI NUMBER</label>
            <select
              className="form-select border-primary"
              value={filters.vanchiNumber}
              onChange={(e) => handleFilterChange('vanchiNumber', e.target.value)}
            >
              <option value="">Select Vanchi No.</option>
              {filteredVanchiNumbers.map((vanchi, index) => (
                <option key={vanchi.vanchicd || index} value={vanchi.vanchino}>
                  {vanchi.vanchino || '-'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Row 2 */}
        <div className="d-flex gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">PERIODIC REPORT *</label>
            <select
              className="form-select border-primary"
              value={filters.periodicReport}
              onChange={(e) => handleFilterChange('periodicReport', e.target.value)}
              required
            >
              <option value="recent">Recent Transactions</option>
              <option value="daily">Daily Report</option>
              <option value="weekly">Weekly Report</option>
              <option value="monthly">Monthly Report</option>
              <option value="yearly">Yearly Report</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">FROM DATE</label>
            <input
              type="date"
              className="form-select border-primary"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              placeholder="From Date"
              disabled={filters.periodicReport !== 'custom'}
            />
          </div>
          <div style={{ width: '17%', minWidth: '200px' }}>
            <label className="form-label text-primary">TO DATE</label>
            <input
              type="date"
              className="form-select border-primary"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              placeholder="To Date"
              disabled={filters.periodicReport !== 'custom'}
            />
          </div>
          <div style={{ flex: 1, minWidth: '400px' }}>
            <label className="form-label text-primary">&nbsp;</label>
            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={handleSearch}>
                SEARCH
              </button>
              <button
                className={`btn ${canExport && !isExporting ? 'btn-success' : 'btn-outline-secondary'}`}
                onClick={handleExportExcel}
                disabled={!canExport || isExporting}
                title={canExport && !isExporting ? 'Export current table data to Excel' : !canExport ? 'Search for data first to enable export' : 'Exporting...'}
              >
                {isExporting ? 'Exporting...' : 'Export as EXCEL'}
              </button>
              <button
                className={`btn ${canExport && !isExporting ? 'btn-danger' : 'btn-outline-secondary'}`}
                onClick={handleExportPDF}
                disabled={!canExport || isExporting}
                title={canExport && !isExporting ? 'Export current table data to PDF' : !canExport ? 'Search for data first to enable export' : 'Exporting...'}
              >
                {isExporting ? 'Exporting...' : 'Export as PDF'}
              </button>
              <button className="btn btn-secondary px-4 py-2" onClick={handleClear}>
                CLEAR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DataTable */}
      {showTable && (
        <div style={{ height: 'calc(100% - 120px)', backgroundColor: '#e9e6ec' }}>
          <DataTable
            columns={columns}
            data={filteredData}
            loading={loading}
            pagination={pagination}
            onPageChange={handlePageChange}
            onSort={handleSort}
            searchable={false}
            totalCountOverride={totalFilteredCount}
          />
        </div>
      )}
    </div>
  );
};

export default Report;
